import * as React from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import FailureSnackbar from '../../../../../../../shared/feedback/FailureSnackbar';
import {
    useEditableFilenameForm,
    EDITABLE_FILENAME_FORM_ID
} from '../state/use-editable-filename-form';

const TRANSITION = '600ms ease-in-out';

type EditableFilenameProps = {
    filename: string;
    canEdit: boolean;
    onSave: (newFilename: string) => Promise<any>;
};

const Container = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0
});

/**
 * Both FilenameTextWrapper and FormWrapper stack in the same grid cell
 * so toggling between them only animates opacity + transform (GPU-composited,
 * no layout recalculation, no jank).
 *
 * The container itself animates max-width so surrounding elements (buttons,
 * counter) slide smoothly when the slot grows/shrinks between filename and input.
 */
const ContentSlot = styled('div')({
    display: 'grid',
    minWidth: 0,
    '& > *': {
        gridArea: '1 / 1'
    }
});

const FilenameTextWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden',
    maxWidth: '200px',
    transition: [
        'opacity ' + TRANSITION,
        'transform ' + TRANSITION,
        'max-width ' + TRANSITION
    ].join(', '),
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',

    '&[data-editing="true"]': {
        opacity: 0,
        transform: 'translateY(-8px)',
        pointerEvents: 'none',
        maxWidth: 0
    }
});

const FilenameHeading = styled('h3')({
    margin: 0,
    fontSize: '14px',
    fontWeight: 400,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    borderRadius: '3px',
    padding: '0 2px'
});

const FormWrapper = styled('form')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    overflow: 'hidden',
    maxWidth: 0,
    transition: [
        'opacity ' + TRANSITION,
        'transform ' + TRANSITION,
        'max-width ' + TRANSITION
    ].join(', '),
    opacity: 0,
    transform: 'translateY(8px)',
    pointerEvents: 'none',

    '&[data-editing="true"]': {
        opacity: 1,
        transform: 'translateY(0)',
        pointerEvents: 'auto',
        maxWidth: '240px'
    }
});

const FilenameInput = styled('input')({
    margin: 0,
    fontSize: '14px',
    fontWeight: 400,
    color: '#333',
    backgroundColor: '#fff',
    fontFamily: 'inherit',
    border: '1px solid #bbb',
    borderRadius: '4px',
    padding: '2px 8px',
    outline: 'none',
    width: '180px',
    transition: 'border-color ' + TRANSITION,
    '&:focus': {
        borderColor: '#1976d2'
    }
});

/**
 * Edit button and Save/Cancel buttons stack in the same grid cell,
 * cross-fading with only opacity + transform.
 */
const ActionsSlot = styled('div')({
    display: 'grid',
    alignItems: 'center',
    overflow: 'hidden',
    transition: 'max-width ' + TRANSITION,
    maxWidth: '34px',
    '& > *': {
        gridArea: '1 / 1'
    },
    '&[data-editing="true"]': {
        maxWidth: '200px'
    }
});

const ActionGroup = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    transition: ['opacity ' + TRANSITION, 'transform ' + TRANSITION].join(', '),
    opacity: 0,
    transform: 'scale(0.8)',
    pointerEvents: 'none',

    '&[data-visible="true"]': {
        opacity: 1,
        transform: 'scale(1)',
        pointerEvents: 'auto'
    }
});

export function EditableFilename({ filename, canEdit, onSave }: EditableFilenameProps) {
    let {
        isEditing,
        displayedFilename,
        saveError,
        dismissSaveError,
        inputRef,
        handleEdit,
        handleSubmit,
        handleCancel,
        handleKeyDown
    } = useEditableFilenameForm({ filename, onSave });

    let editing = isEditing.toString();

    return (
        <Container>
            <ContentSlot>
                <FilenameTextWrapper data-editing={editing}>
                    <FilenameHeading id="image-preview-title">{displayedFilename}</FilenameHeading>
                </FilenameTextWrapper>

                <FormWrapper
                    id={EDITABLE_FILENAME_FORM_ID}
                    onSubmit={handleSubmit}
                    data-editing={editing}
                >
                    <FilenameInput
                        ref={inputRef}
                        type="text"
                        name="filename"
                        defaultValue={displayedFilename}
                        key={displayedFilename}
                        onKeyDown={handleKeyDown}
                        aria-label="Edit filename"
                    />
                </FormWrapper>
            </ContentSlot>

            {canEdit && (
                <ActionsSlot data-editing={editing}>
                    <ActionGroup data-visible={(!isEditing).toString()}>
                        <IconButton
                            aria-label="Edit filename"
                            onClick={handleEdit}
                            size="small"
                            type="button"
                            sx={{ color: '#666' }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </ActionGroup>

                    <ActionGroup data-visible={editing}>
                        <Button
                            aria-label="Save filename"
                            size="small"
                            type="submit"
                            form={EDITABLE_FILENAME_FORM_ID}
                            variant="contained"
                            startIcon={<SaveIcon fontSize="small" />}
                        >
                            {Translate('image-processing/modal/title/button-label/save')}
                        </Button>
                        <Button
                            aria-label="Cancel editing"
                            onClick={handleCancel}
                            size="small"
                            type="button"
                            variant="outlined"
                            startIcon={<CloseIcon fontSize="small" />}
                        >
                            {Translate('image-processing/modal/title/button-label/cancel')}
                        </Button>
                    </ActionGroup>
                </ActionsSlot>
            )}
            <FailureSnackbar
                open={saveError}
                onClose={dismissSaveError}
                text={Translate('image-processing/image-preview/error/save-filename')}
            />
        </Container>
    );
}
